import { useMemo, useEffect, useRef, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import * as offerSelectors from '@makemydeal/dr-dash-store/selectors/offerSelectors';
import * as vehicleSelectors from '@makemydeal/dr-dash-store/selectors/vehicleSelectors';
import * as contractingSelectors from '@makemydeal/dr-dash-store/selectors/contractingSelectors';
import * as contractingActionCreators from '@makemydeal/dr-dash-store/actions/contractingActionCreators';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import * as contractingUtils from '@makemydeal/dr-dash-store/utils/contractingUtils';
import { PRINCIPAL_DRIVER_NAME_MAX_LENGTH } from '@makemydeal/dr-dash-store/utils/contractFieldErrorConfigs';
import * as dealScreenErrorActionCreators from '@makemydeal/dr-dash-store/actions/dealScreenErrorActionCreators';
import { paymentSelectors, actions, selectors, actionCreators } from '@makemydeal/dr-offer-redux';
import type { IVehicle } from '@makemydeal/dr-platform-types';
import { CASH, DcVppVehicleUsage, OdometerMileageTypes } from '@makemydeal/dr-platform-types';
import { VEHICLE_ENTITY } from '@makemydeal/dr-shared-types';
import { changeVehicleCreators, changeVehicleSelectors } from '@makemydeal/dr-activities-change-vehicle';
import { CONTRACTING, DEFAULT, DEFAULT_ERROR_STATE, useFormConfigValidations } from '@makemydeal/dr-dash-ui-utils';

import { vehicleInfoFormKeys } from './formContext';
import { getMissingBookoutFields, isObjectEqual, isSameValue, odometerValidator, gvwrValidator, validateForm } from './helper';
import type { FMErrors, FMInputErrors, FMNames, FMProps, FMValueTypes, PaymentState } from '../types';
import { FMField } from '../types';
import {
    GENERIC_LAST_FIELD_CHANGED_ERROR,
    STOCK_NUMBER_DEFAULT_ERROR_MSG,
    STOCK_NUMBER_USED_ERROR_MSG,
    STOCK_NUMBER_USER_PERMISSIONS_ERROR_MSG
} from '../../constants';
import { REQUIRED_FIELD_LABELS } from '../constants';

const { UPDATED_VEHICLE_INFO_DETAILS, UPDATED_OFFER_PRICE_IMMEDIATE, UPDATED_RETAIL_PRICE_OVERRIDE } = actions;

type VehicleContractValidationValue = FMValueTypes | Record<string, string>;

function useForm(hideDrawer: () => void) {
    const dispatch = useDispatch();
    const [canApply, setCanApply] = useState(false);
    const [, setVersion] = useState(0);
    const [formVersion, setFormVersion] = useState(0);
    const errorContext = useRef<FMErrors>({});
    const contractFormErrorsRef = useRef<FMInputErrors>({});
    const [isStockEditable, setIsStockEditable] = useState(false);
    const [isTrimEditable, setIsTrimEditable] = useState(false);
    const [showContractingErrorAlert, setShowContractingErrorAlert] = useState(false);
    const { getServicesBff, getCommonOrgId } = changeVehicleSelectors.additionalSelectors;
    const bffEndpoint = useSelector(getServicesBff);
    const orgId = useSelector(getCommonOrgId);
    const isStockNumberAvailable = useSelector(changeVehicleSelectors.getIsStockNumberAvailable);
    const isSearchError = useSelector(changeVehicleSelectors.getIsSearchError);
    const isPermissionError = useSelector(changeVehicleSelectors.getIsPermissionError);
    const isFetching = useSelector(changeVehicleSelectors.getIsFetching);
    const { actionForPayment, isCalculating, error: paymentError } = useSelector(paymentSelectors.getPaymentState) as PaymentState;
    const {
        condition: rawCondition,
        cost = 0,
        invoiceAmount = 0,
        mileage,
        wholesaleValue = 0,
        wholesaleBookSource,
        wholesaleValueType,
        stockNumber,
        cylinders,
        odometerMileageType,
        driveLine,
        engine,
        exteriorColor,
        fuel,
        interiorColor,
        location,
        transmission,
        transmissionType,
        trim,
        brakingMechanism,
        steeringMechanism,
        isAirConditioningIncluded,
        grossVehicleWeightRating,
        bookouts,
        vehicleUse,
        principalDriverName
    } = useSelector(vehicleSelectors.getVehicle);
    // Inventory Type defaults to "new" when the deal has no condition; otherwise the deal's value
    // (lower-cased so it matches the Inventory Type dropdown options). See US2032152.
    const condition = (rawCondition || 'new').toLowerCase();
    const sellingPrice = useSelector(offerSelectors.getSellingPrice);
    const msrp = useSelector(offerSelectors.getRetailPriceOverrideWithFallback);
    const offerType = useSelector(selectors.getCurrentOfferType);
    const isGCDynamicValidationsEnabled = useSelector(featureToggleSelectors.isGCDynamicValidationsEnabled);
    const isBookoutErrorsOnOpenEnabled = useSelector(featureToggleSelectors.isBookoutErrorsOnOpenEnabled);
    const isCashOfferPriceFallbackEnabled = useSelector(featureToggleSelectors.isCashOfferPriceFallbackEnabled);
    const isDrawerShown = useSelector(vehicleSelectors.showEditVehicle);
    const isQuoteWithoutVehicle = useSelector(vehicleSelectors.getIsQuoteWithoutVehicle);
    const allContractErrors = useSelector(contractingSelectors.getContractErrorList);
    const contractErrorFields = useSelector(contractingSelectors.getContractValidationEntityErrorFields(VEHICLE_ENTITY));
    const isContractingContext = useSelector(contractingSelectors.getIsContractingContext);
    const lenderName = useSelector(contractingSelectors.getLenderProgramName);
    const buyer = useSelector(offerReduxSelectors.getShopperInfo);
    const getContractEntityConfigMap = useMemo(() => {
        return contractingUtils.createContractEntityConfigMap();
    }, []);
    // Change the form error context to contracting if there are contract errors found
    const validationContext = isGCDynamicValidationsEnabled && allContractErrors.length ? CONTRACTING : DEFAULT;
    const vehicleContractingFields: string[] = useMemo(() => Object.keys(getContractEntityConfigMap(VEHICLE_ENTITY, true)), []);
    const {
        formErrors: contractFormHookErrors,
        validateField,
        resetFormErrors,
        validateContractFields
    } = useFormConfigValidations(
        {
            [FMField.cylinders]: { ...DEFAULT_ERROR_STATE },
            [FMField.mileage]: { ...DEFAULT_ERROR_STATE },
            [FMField.vehicleUse]: { ...DEFAULT_ERROR_STATE },
            [FMField.principalDriverName]: { ...DEFAULT_ERROR_STATE }
        },
        { [FMField.cylinders]: [], [FMField.mileage]: [], [FMField.vehicleUse]: [], [FMField.principalDriverName]: [] },
        getContractEntityConfigMap,
        VEHICLE_ENTITY
    );

    // Few vehicle contracting fields are triggered based on buyer state or offer type, so need to set those in context
    const contractingFieldsContext = useMemo(() => ({ buyerState: buyer.state, offerType }), [buyer.state, offerType]);

    const getContractingErrorFieldsWithErrors = (errors: typeof contractFormHookErrors): string[] => {
        return vehicleContractingFields.filter((fieldName) => {
            const fieldError = errors[fieldName as keyof typeof contractFormHookErrors];
            return fieldError && 'hasError' in fieldError && fieldError.hasError;
        });
    };

    // Check if there are any contracting errors present for vehicle contracting fields
    const checkContractingErrors = (errors: typeof contractFormHookErrors): boolean => {
        return !!getContractingErrorFieldsWithErrors(errors).length;
    };

    // Map contracting error fields to formManager error fields
    const mapContractErrorsToFormErrors = ({
        fieldKey,
        errorMessage
    }: {
        fieldKey: string;
        errorMessage: string;
    }): FMInputErrors => {
        const errors: FMInputErrors = {};
        if (fieldKey === 'odometerMileageType') {
            errors[FMField.mileage] = errorMessage;
        } else if (fieldKey === 'bookoutOrInvoice') {
            errors[FMField.invoiceAmount] = errorMessage;
            if (!isQuoteWithoutVehicle && offerType !== CASH) {
                errors[FMField.wholesaleBookSource] = errorMessage;
                errors[FMField.wholesaleValue] = errorMessage;
            }
        } else {
            errors[fieldKey as FMNames] = errorMessage;
        }
        return errors;
    };

    // Validate red dot errors for vehicle contracting fields
    const updateVehicleRedDotErrors = () => {
        if (isGCDynamicValidationsEnabled) {
            const errorState = Object.fromEntries(
                vehicleContractingFields.map((fieldName) => [
                    fieldName,
                    !!contractFormHookErrors[fieldName as keyof typeof contractFormHookErrors]?.hasError
                ])
            );
            dispatch(dealScreenErrorActionCreators.setErrorFields(VEHICLE_ENTITY, errorState));
        }
    };

    // Moving contract error fields to useFormConfigValidations
    useEffect(() => {
        // Here need to check validate the full array because with buyer state change the length could be same but the fields could be different
        if (isGCDynamicValidationsEnabled && contractErrorFields.length) {
            const contractFieldValuePairs: Record<string, VehicleContractValidationValue> = {};
            vehicleContractingFields.forEach((fieldName) => {
                contractFieldValuePairs[fieldName] = getMappedValidateFieldValue(
                    fieldName as FMNames,
                    formManager.values[fieldName as FMField]
                );
            });
            validateContractFields(contractFieldValuePairs, contractingFieldsContext);
        }
    }, [contractErrorFields.length, contractingFieldsContext, formVersion]);

    // Moving useFormConfigValidations errors to local formManager.error state
    useEffect(() => {
        if (!isGCDynamicValidationsEnabled) return;
        const hasContractingErrors = checkContractingErrors(contractFormHookErrors);
        setShowContractingErrorAlert(hasContractingErrors);

        const formContractErrors: FMInputErrors = {};

        for (const entry of Object.entries(contractFormHookErrors)) {
            const [fieldKey, { errorMessage }] = entry;
            if (!errorMessage) continue;
            Object.assign(formContractErrors, mapContractErrorsToFormErrors({ fieldKey, errorMessage }));
        }
        // Persist contracting errors so other effects (e.g. paymentError) can preserve them
        contractFormErrorsRef.current = formContractErrors;
        // Resetting red dot errors if no contracting errors present with buyer state change
        updateVehicleRedDotErrors();
        formManager.error = { ...errorContext.current[offerType], ...formContractErrors };
    }, [contractFormHookErrors]);

    const formManager = useMemo<FMProps>(() => {
        if (!errorContext.current[offerType]) {
            errorContext.current[offerType] = {};
        }

        return {
            touched: {},
            error: paymentError ? errorContext.current[offerType] : {},
            values: {
                msrp,
                sellingPrice,
                invoiceAmount,
                cost,
                condition,
                mileage: String(mileage),
                wholesaleValue,
                wholesaleBookSource,
                wholesaleValueType,
                stockNumber,
                cylinders,
                odometerMileageType: odometerMileageType === OdometerMileageTypes.Actual,
                driveLine,
                engine,
                exteriorColor,
                fuel,
                interiorColor,
                location,
                transmission,
                transmissionType,
                trim,
                brakingMechanism,
                steeringMechanism,
                isAirConditioningIncluded: isAirConditioningIncluded != null ? String(isAirConditioningIncluded) : undefined,
                grossVehicleWeightRating,
                vehicleUse,
                principalDriverName
            },
            initialValues: {
                msrp,
                sellingPrice,
                invoiceAmount,
                cost,
                condition,
                mileage: String(mileage),
                driveLine,
                engine,
                exteriorColor,
                fuel,
                interiorColor,
                location,
                transmission,
                transmissionType,
                trim,
                brakingMechanism,
                steeringMechanism,
                isAirConditioningIncluded: isAirConditioningIncluded != null ? String(isAirConditioningIncluded) : undefined,
                grossVehicleWeightRating,
                wholesaleValue,
                wholesaleBookSource,
                wholesaleValueType,
                stockNumber,
                cylinders,
                odometerMileageType: odometerMileageType === OdometerMileageTypes.Actual,
                vehicleUse,
                principalDriverName
            },
            formVersion
        };
    }, [
        offerType,
        paymentError,
        msrp,
        sellingPrice,
        invoiceAmount,
        cost,
        condition,
        mileage,
        wholesaleValue,
        wholesaleBookSource,
        wholesaleValueType,
        stockNumber,
        cylinders,
        formVersion,
        exteriorColor,
        fuel,
        interiorColor,
        location,
        transmission,
        transmissionType,
        trim,
        brakingMechanism,
        steeringMechanism,
        isAirConditioningIncluded,
        grossVehicleWeightRating,
        vehicleUse,
        principalDriverName
    ]);

    const resetStateAndHideDrawer = useCallback(() => {
        errorContext.current[offerType] = {};
        formManager.error = {};
        setCanApply(false);
        hideDrawer();
    }, [formManager, hideDrawer, offerType]);

    const updateAndReset = useCallback(() => {
        setIsStockEditable(false);
        setIsTrimEditable(false);
        const { payload, withPayment } = validateForm(formManager.values, formManager.touched);
        dispatch(
            actionCreators.updateVehicleInfoDetails(
                {
                    retailPrice: payload.retailPrice,
                    sellingPrice:
                        isCashOfferPriceFallbackEnabled && !formManager.touched[FMField.sellingPrice]
                            ? undefined
                            : (payload.sellingPrice as number),
                    vehicle: payload.vehicle as unknown as IVehicle
                },
                withPayment
            )
        );

        if (isContractingContext) {
            dispatch(contractingActionCreators.refreshContractValidationErrors());
        }

        if (!withPayment) {
            resetStateAndHideDrawer();
        }
        updateVehicleRedDotErrors();
        resetFormErrors();
    }, [
        dispatch,
        formManager.touched,
        formManager.values,
        isCashOfferPriceFallbackEnabled,
        isContractingContext,
        resetFormErrors,
        resetStateAndHideDrawer
    ]);

    const checkBookSourceErrors = (formManager: FMProps): boolean => {
        const { error } = formManager;

        delete error.wholesaleValue;
        delete error.wholesaleBookSource;

        if (isQuoteWithoutVehicle) {
            return false;
        }

        const missingFields = getMissingBookoutFields(formManager.values);
        missingFields.forEach((name) => {
            error[name] = 'Required';
        });

        return !!missingFields.length;
    };

    const getMissingRequiredFields = (): FMNames[] =>
        isBookoutErrorsOnOpenEnabled && !isQuoteWithoutVehicle ? getMissingBookoutFields(formManager.values) : [];

    // The input renders its own over-limit message; the form only needs to know so Apply stays blocked
    const isPrincipalDriverNameTooLong = (formManager: FMProps): boolean =>
        String(formManager.values[FMField.principalDriverName] ?? '').length > PRINCIPAL_DRIVER_NAME_MAX_LENGTH;

    const isBookoutOrInvoiceFieldName = (name: FMNames | string) => {
        return (
            name === FMField.invoiceAmount ||
            name === FMField.wholesaleValue ||
            name === FMField.wholesaleBookSource ||
            name === FMField.bookoutOrInvoice
        );
    };

    const getMappedValidateFieldValue = (name: FMNames, value: FMValueTypes) => {
        let validationValue: VehicleContractValidationValue = value;
        if (name === FMField.odometerMileageType) {
            validationValue = value ? OdometerMileageTypes.Actual : OdometerMileageTypes.Estimate;
        } else if (name === FMField.principalDriverName) {
            validationValue = {
                principalDriverName: formManager.values[FMField.principalDriverName] as string,
                vehicleUse: formManager.values[FMField.vehicleUse] as string,
                offerType,
                lenderName
            };
        } else if (isBookoutOrInvoiceFieldName(name)) {
            validationValue = {
                invoiceAmount: formManager.values[FMField.invoiceAmount] as string,
                bookoutValue: formManager.values[FMField.wholesaleValue] as string,
                bookoutSource: formManager.values[FMField.wholesaleBookSource] as string
            };
        }
        return validationValue;
    };

    // Validate contracting fields on change of field value
    const checkContractingErrorsOnChange = (name: FMNames, value: FMValueTypes) => {
        if (!isGCDynamicValidationsEnabled || !allContractErrors.length) {
            return false;
        }
        const validationNameField = isBookoutOrInvoiceFieldName(name) ? 'bookoutOrInvoice' : name;
        const errorState = validateField(
            validationNameField,
            getMappedValidateFieldValue(name, value),
            validationContext,
            contractingFieldsContext
        );
        const nextErrors = { ...contractFormHookErrors, [validationNameField]: errorState };

        // The driver-name rule keys off Primary Use, so re-run it when that moves
        if (name === FMField.vehicleUse) {
            nextErrors[FMField.principalDriverName] = validateField(
                FMField.principalDriverName,
                getMappedValidateFieldValue(FMField.principalDriverName, formManager.values[FMField.principalDriverName]),
                validationContext,
                contractingFieldsContext
            );
        }

        return checkContractingErrors(nextErrors);
    };

    const getBookoutValueForSource = (bookSource: string): number | undefined => {
        if (!bookouts || !bookSource || !bookouts[bookSource]) {
            return undefined;
        }

        return bookouts[bookSource].bookoutValue;
    };

    const isChanged = (name: FMNames, value: FMValueTypes) => !isSameValue(value, formManager.initialValues[name]);

    const changeHandler = (name: FMNames, value: FMValueTypes) => {
        formManager.touched[name] = isChanged(name, value);
        formManager.values[name] = value;

        if (name === FMField.wholesaleBookSource) {
            const bookoutValue = getBookoutValueForSource(value as string);

            formManager.values.wholesaleValue = bookoutValue || 0;
            formManager.touched.wholesaleValue = bookoutValue !== formManager.initialValues.wholesaleValue;

            formManager.values.wholesaleValueType = '';
            formManager.touched.wholesaleValueType = isChanged(FMField.wholesaleValueType, '');
        }

        // Hidden for non-business use, so clear it rather than save a name the user cannot see
        if (name === FMField.vehicleUse && value !== DcVppVehicleUsage.BUSINESS) {
            formManager.values.principalDriverName = '';
            formManager.touched.principalDriverName = isChanged(FMField.principalDriverName, '');
        }

        const hasChanged = !isObjectEqual(formManager.values, formManager.initialValues);
        const hasSourceErrors = checkBookSourceErrors(formManager);
        const hasContractingErrors = checkContractingErrorsOnChange(name, value);

        setCanApply(hasChanged && !hasSourceErrors && !hasContractingErrors && !isPrincipalDriverNameTooLong(formManager));
        setVersion(Date.now());
    };

    const handleClick = (type: 'APPLY' | 'CANCEL') => {
        setCanApply(false);

        if (type === 'CANCEL') {
            resetEditable();
            hideDrawer();
            setFormVersion(Date.now());
            return;
        }

        if (formManager.error[FMField.stockNumber]) {
            setCanApply(true);
            return;
        }

        vehicleInfoFormKeys.forEach((key) => {
            if (key !== FMField.stockNumber && formManager.touched[key]) {
                errorContext.current[offerType][key] = GENERIC_LAST_FIELD_CHANGED_ERROR;
            }
        });

        if (formManager.touched.stockNumber && formManager.values.stockNumber) {
            const formStockNumber = formManager.values.stockNumber.toString();
            dispatch(changeVehicleCreators.searchStockNumber(formStockNumber, orgId, bffEndpoint));
        } else {
            updateAndReset();
        }
    };

    const handleBlur = (name: FMNames, value: FMValueTypes): void => {
        let validatedValue: FMValueTypes = value;
        switch (name) {
            case FMField.mileage:
                validatedValue = odometerValidator(value as string);
                break;
            case FMField.grossVehicleWeightRating:
                validatedValue = gvwrValidator(value as string);
                break;
        }
        if (validatedValue !== value) {
            changeHandler(name, validatedValue);
        }
    };

    const handleClickEditStock = () => {
        setIsStockEditable(true);
    };

    const handleClickEditTrim = () => {
        setIsTrimEditable(true);
        setIsStockEditable(true);
    };

    const resetEditable = () => {
        setIsStockEditable(false);
        setIsTrimEditable(false);
        formManager.values.stockNumber = formManager.initialValues.stockNumber;
        formManager.values.trim = formManager.initialValues.trim;
        delete formManager.error[FMField.stockNumber];
        delete formManager.error[FMField.trim];
        delete errorContext.current[offerType][FMField.stockNumber];
        delete errorContext.current[offerType][FMField.trim];
    };

    const setStockNumberError = (error: string) => {
        errorContext.current[offerType][FMField.stockNumber] = error;
        setCanApply(true);
    };

    useEffect(() => {
        if (typeof isStockNumberAvailable === 'undefined' || isFetching) return;

        if (isPermissionError) {
            setStockNumberError(STOCK_NUMBER_USER_PERMISSIONS_ERROR_MSG);
            return;
        }

        if (isSearchError) {
            setStockNumberError(STOCK_NUMBER_DEFAULT_ERROR_MSG);
            return;
        }

        if (!isStockNumberAvailable) {
            setStockNumberError(STOCK_NUMBER_USED_ERROR_MSG);
            return;
        }

        updateAndReset();
    }, [isStockNumberAvailable, isPermissionError]);

    useEffect(() => {
        const isStockNumberAvailableError = isStockNumberAvailable === false;
        const baseErrors = paymentError || isSearchError || isStockNumberAvailableError ? errorContext.current[offerType] : {};
        formManager.error = { ...baseErrors, ...contractFormErrorsRef.current };
        setVersion(Date.now());
    }, [formManager, offerType, paymentError, isSearchError, isStockNumberAvailable]);

    useEffect(() => {
        if (
            actionForPayment === UPDATED_VEHICLE_INFO_DETAILS ||
            actionForPayment === UPDATED_OFFER_PRICE_IMMEDIATE ||
            actionForPayment === UPDATED_RETAIL_PRICE_OVERRIDE
        ) {
            setVersion(Date.now());
            if (paymentError) {
                switch (actionForPayment) {
                    case UPDATED_OFFER_PRICE_IMMEDIATE:
                        errorContext.current[offerType] = {
                            ...errorContext.current[offerType],
                            sellingPrice: GENERIC_LAST_FIELD_CHANGED_ERROR
                        };
                        break;
                    case UPDATED_RETAIL_PRICE_OVERRIDE:
                        errorContext.current[offerType] = {
                            ...errorContext.current[offerType],
                            msrp: GENERIC_LAST_FIELD_CHANGED_ERROR
                        };
                        break;
                }
                formManager.error = { ...errorContext.current[offerType], ...contractFormErrorsRef.current };
            } else if (!isCalculating) {
                resetStateAndHideDrawer();
            }
        }
    }, [actionForPayment, paymentError, formManager, hideDrawer, isCalculating, offerType, resetStateAndHideDrawer]);

    // Declared after the effects that reassign formManager.error, so the gaps found on open stay flagged
    useEffect(() => {
        if (!isDrawerShown) return;
        const missingFields = getMissingRequiredFields();
        if (!missingFields.length) return;
        missingFields.forEach((name) => {
            formManager.error[name] = formManager.error[name] || 'Required';
        });
        setVersion(Date.now());
    }, [isDrawerShown, formManager, offerType, contractFormHookErrors, paymentError, isSearchError, isStockNumberAvailable]);

    return {
        form: formManager,
        canApply,
        changeHandler,
        handleClick,
        hideDrawer,
        isStockEditable,
        handleClickEditStock,
        isTrimEditable,
        handleClickEditTrim,
        resetEditable,
        handleBlur,
        showContractingErrorAlert,
        missingRequiredFields: getMissingRequiredFields().map((name) => REQUIRED_FIELD_LABELS[name])
    };
}

export default useForm;
